import { createReduxHistoryContext, RouterState } from 'redux-first-history';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { EndpointDefinitions } from '@reduxjs/toolkit/dist/query';
import { CombinedState } from '@reduxjs/toolkit/dist/query/core/apiState';
import { createBrowserHistory } from 'history';
import { persistStore, persistReducer, createTransform } from 'redux-persist';
import storageSession from 'redux-persist/lib/storage/session';

import { popupAlertsMiddleware } from './middleware/popup-alerts-middleware';
import appReducer, { AppState, initialState } from './modules/app';
import { apiSlice } from './services';

const isProduction = false;

const { createReduxHistory, routerMiddleware, routerReducer } = createReduxHistoryContext({
  history: createBrowserHistory(),
  savePreviousLocations: 2,
});

export type ApplicationState = Readonly<{
  app: AppState;
  api: CombinedState<EndpointDefinitions, never, 'api'>;
  router: RouterState;
}>;
const paramsToSave = createTransform(
  // Transform state before it's serialized and persisted.
  (inboundState, key) => {
    if (key === 'app') {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return { applicationId: inboundState?.applicationId };
    }

    return inboundState;
  },
  // Transform state being rehydrated.
  (outboundState) => ({
    ...initialState,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    ...(outboundState?.applicationId && { applicationId: outboundState?.applicationId }),
  }),
  // Specify which reducer this transform applies to.
  { whitelist: ['app'] },
);

const persistConfig = {
  key: 'root',
  storage: storageSession,
  version: 1,
  whitelist: ['app'],
  transforms: [paramsToSave],
};

const rootReducer = combineReducers({
  app: appReducer,
  [apiSlice.reducerPath]: apiSlice.reducer,
  router: routerReducer,
});

const store = configureStore({
  reducer: persistReducer(persistConfig, rootReducer),
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(apiSlice.middleware as any, routerMiddleware, popupAlertsMiddleware),
  devTools: !isProduction,
});

const history = createReduxHistory(store);
const persistor = persistStore(store);

export { store, history, persistor };
